import { type ThemeConfig, theme } from "antd"

import { type ResolvedAppearance } from "./appearance/appearance-preference"

const colors = {
  primary: "#2A7D8C",
  placeholder: "#9E9E9E",
  border: "#E5E7EB",
  // The container border is too faint to double as a control outline: an
  // unchecked box read as plain text until hovered. WCAG 1.4.11 wants 3:1.
  controlBorder: "#64748B"
}

export const defaultTheme: ThemeConfig = {
  token: {
    fontFamily: `'Manrope', sans-serif`,
    fontWeightStrong: 500,
    colorBorder: colors.border,
    colorBorderSecondary: colors.border,

    // borderRadius: 12,
    // radiusBase: 4,
    // controlRadius: 4,

    colorPrimary: colors.primary,

    // responsive title sizes
    fontSizeHeading1: "clamp(24px, 6vw, 38px)" as any,
    fontSizeHeading2: "clamp(20px, 5vw, 30px)" as any,
    fontSizeHeading3: "clamp(18px, 4vw, 24px)" as any,
    fontSizeHeading4: "clamp(16px, 3.5vw, 20px)" as any,
    fontSizeHeading5: "clamp(14px, 3vw, 16px)" as any,

    //
    colorTextPlaceholder: colors.placeholder,
    //
    colorTextDisabled: colors.placeholder
  },

  components: {
    Tag: {
      borderRadius: 12,
      borderRadiusXS: 12,
      borderRadiusSM: 12,
      borderRadiusLG: 12
    },
    Typography: {
      fontWeightStrong: 600,
      lineHeight: "20px" as any,
      titleMarginBottom: "0.2rem"
    },
    Progress: {
      defaultColor: colors.primary
    },
    Table: {
      bodySortBg: "#fafafa",
      borderColor: "#f0f0f0",
      // row
      rowHoverBg: "#fafafa",
      // header
      headerBg: "#F9FAFB",
      headerColor: "#000000",
      //
      footerBg: "#ffffff"
    },
    Layout: {
      bodyBg: "#F9FAFB",
      headerBg: "#FFFFFF"
      //headerPadding: '16px 32px',
    },
    Button: {
      defaultShadow: "none",
      primaryShadow: "none"
    },
    Checkbox: {
      colorBorder: colors.controlBorder
    },
    DatePicker: {
      // A 12-hour time panel has 12 hour rows; the default 8-row column
      // height (28px * 8) made the 4 past the fold reachable only by
      // scrolling. 28px * 12 shows every hour (and the shorter minute/
      // meridiem columns) with nothing to scroll to.
      timeColumnHeight: 28 * 12
    }
  }
}

const dark = {
  page: "#151A21",
  surface: "#1D2430",
  raised: "#232C3A",
  subtle: "#1F2631",
  hover: "#263040",
  selected: "#1F3A40",
  selectedHover: "#244650",
  border: "#283241",
  controlOutline: "#3A4658",
  controlBorder: "#66758C",
  text: "#E6EAF0",
  textSecondary: "#A3AEBE",
  textTertiary: "#8D98AA",
  textQuaternary: "#6B778A",
  placeholder: "#76829A",
  // The brand teal still fills buttons (white on it is 4.8:1), but as text on
  // the dark surfaces it is 3.3:1, so text and links take a lighter teal.
  primaryText: "#5CB8C7",
  primaryTextHover: "#7CCAD6"
}

// Layered over defaultTheme with antd's dark algorithm; every value here is
// a map token, so it wins over what the algorithm derives from the seed.
const darkTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    colorBgLayout: dark.page,
    colorBgContainer: dark.surface,
    colorBgElevated: dark.raised,
    colorBgSpotlight: "#3A4658",
    colorBgMask: "rgba(5, 8, 12, 0.6)",
    colorBorder: dark.controlOutline,
    colorBorderSecondary: dark.border,
    colorSplit: dark.border,
    colorFillAlter: dark.subtle,
    colorText: dark.text,
    colorTextHeading: dark.text,
    colorTextSecondary: dark.textSecondary,
    colorTextTertiary: dark.textTertiary,
    colorTextQuaternary: dark.textQuaternary,
    colorTextDescription: dark.textSecondary,
    colorTextPlaceholder: dark.placeholder,
    colorTextDisabled: dark.textQuaternary,
    colorPrimary: colors.primary,
    colorPrimaryHover: "#348F9F",
    colorPrimaryActive: "#246C79",
    colorPrimaryBg: dark.selected,
    colorPrimaryBgHover: dark.selectedHover,
    colorPrimaryBorder: "#2E5A63",
    colorPrimaryBorderHover: "#3A7480",
    colorPrimaryText: dark.primaryText,
    colorPrimaryTextHover: dark.primaryTextHover,
    colorPrimaryTextActive: dark.primaryText,
    colorLink: dark.primaryText,
    colorLinkHover: dark.primaryTextHover,
    colorLinkActive: dark.primaryText
  },
  components: {
    Table: {
      bodySortBg: dark.subtle,
      borderColor: dark.border,
      rowHoverBg: dark.raised,
      rowSelectedBg: dark.selected,
      rowSelectedHoverBg: dark.selectedHover,
      headerBg: "#222A36",
      headerColor: dark.text,
      headerSortActiveBg: "#263040",
      headerSortHoverBg: "#263040",
      footerBg: dark.surface,
      fixedHeaderSortActiveBg: "#263040"
    },
    Layout: {
      bodyBg: dark.page,
      headerBg: dark.surface,
      siderBg: dark.surface,
      footerBg: dark.page
    },
    Checkbox: { colorBorder: dark.controlBorder },
    Radio: { colorBorder: dark.controlBorder },
    Menu: {
      itemSelectedColor: dark.primaryText,
      itemSelectedBg: dark.selected,
      itemHoverBg: dark.hover,
      itemActiveBg: dark.selected
    },
    Tabs: {
      itemSelectedColor: dark.primaryText,
      itemHoverColor: dark.primaryTextHover,
      itemActiveColor: dark.primaryText,
      inkBarColor: dark.primaryText
    },
    Select: { optionSelectedBg: dark.selected },
    TreeSelect: { nodeSelectedBg: dark.selected },
    Segmented: {
      trackBg: dark.page,
      itemSelectedBg: "#2E3A4B",
      itemColor: dark.textSecondary,
      itemHoverColor: dark.text,
      itemSelectedColor: dark.text
    },
    Tooltip: { colorBgSpotlight: "#3A4658" },
    Steps: { colorPrimary: dark.primaryText },
    Pagination: { itemActiveBg: dark.surface }
  }
}

const mergeComponents = (
  base: ThemeConfig["components"],
  over: ThemeConfig["components"]
): ThemeConfig["components"] => {
  const merged: Record<string, object> = { ...(base as object) }
  Object.entries(over ?? {}).forEach(([name, tokens]) => {
    merged[name] = { ...(merged[name] ?? {}), ...(tokens as object) }
  })
  return merged as ThemeConfig["components"]
}

export const themeForAppearance = (
  resolved: ResolvedAppearance
): ThemeConfig =>
  resolved === "dark"
    ? {
        ...defaultTheme,
        algorithm: darkTheme.algorithm,
        token: { ...defaultTheme.token, ...darkTheme.token },
        components: mergeComponents(
          defaultTheme.components,
          darkTheme.components
        )
      }
    : defaultTheme
